// maintaining this here due to https://github.com/final-form/final-form-calculate/issues/26
import { Decorator, FormApi, getIn } from 'final-form';

type FieldName = string;

type FieldPattern = FieldName | RegExp | FieldName[];

type UpdatesByName = Record<string, (value: any, allValues?: object, prevValues?: object) => any>;

type UpdatesForAll = (value: any, field: string, allValues?: object, prevValues?: object) => Record<string, any>;

type Updates = UpdatesByName | UpdatesForAll;

export interface Calculation {
    field: FieldPattern;
    updates: Updates;
    isEqual?: (a: any, b: any) => boolean;
}

export interface CreateDecoratorOptions {
    forceUpdatesOnBlur?: boolean;
}

class FieldHistory {
    // We don't care about the actual order of fields, so we can use a Set here
    private blurred = new Set<string>();
    private active: string | undefined;

    setActive(field?: string) {
        if (this.active && this.active !== field) {
            this.blurred.add(this.active);
        }

        this.active = field;
    }

    wasBlurred(field: string) {
        return this.blurred.has(field);
    }

    clear() {
        this.blurred.clear();
    }
}

const isPromise = obj => !!obj && (typeof obj === 'object' || typeof obj === 'function') && typeof obj.then === 'function';
const tripleEquals = (a: any, b: any) => a === b;

export const createDecorator =
    (calculations: Calculation[], options?: CreateDecoratorOptions): Decorator =>
    (form: FormApi) => {
        let previousValues = {};
        const fieldHistory = new FieldHistory();

        const unsubscribe = form.subscribe(
            ({ values, active }) => {
                fieldHistory.setActive(active);

                form.batch(() => {
                    const runUpdates = (field: string, isEqual: (a: any, b: any) => boolean, updates: Updates) => {
                        const next = values && getIn(values, field);
                        const previous = previousValues && getIn(previousValues, field);
                        const fieldState = form.getFieldState(field);

                        if ((!isEqual(next, previous) && fieldState?.modified) || fieldHistory.wasBlurred(field)) {
                            if (typeof updates === 'function') {
                                const results = updates(next, field, values, previousValues);

                                if (isPromise(results)) {
                                    results.then(resolved => {
                                        Object.keys(resolved).forEach(destField => {
                                            form.change(destField, resolved[destField]);
                                        });
                                    });
                                } else {
                                    Object.keys(results).forEach(destField => {
                                        form.change(destField, results[destField]);
                                    });
                                }
                            } else {
                                Object.keys(updates).forEach(destField => {
                                    const update = updates[destField];
                                    const result = update(next, values, previousValues);

                                    if (isPromise(result)) {
                                        result.then(resolved => {
                                            form.change(destField, resolved);
                                        });
                                    } else {
                                        form.change(destField, result);
                                    }
                                });
                            }
                        }
                    };

                    const fields = form.getRegisteredFields();

                    calculations.forEach(({ field, isEqual, updates }) => {
                        if (typeof field === 'string') {
                            runUpdates(field, isEqual || tripleEquals, updates);
                        } else {
                            // field is a either array or regex
                            const matches = Array.isArray(field)
                                ? name =>
                                      field.includes(name) ||
                                      field.findIndex((f: any) => f instanceof RegExp && f.test(name)) !== -1
                                : name => field.test(name);

                            fields.forEach(fieldName => {
                                if (matches(fieldName)) {
                                    runUpdates(fieldName, isEqual || tripleEquals, updates);
                                }
                            });
                        }
                    });
                    previousValues = values;
                    fieldHistory.clear();
                });
            },
            { values: true, active: options?.forceUpdatesOnBlur }
        );
        return unsubscribe;
    };
